<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AiBotIntentSetsAPI from 'dashboard/api/aiBot/intentSets';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const draft = inject('aiBotConfigDraft');
const setValue = inject('aiBotConfigSetValue');

const sets = ref([]);
const isLoading = ref(false);

const LABEL_PATTERN = /^[a-z][a-z0-9_]*$/;

const setOptions = computed(() =>
  sets.value.map(set => ({
    value: set.name,
    label: t(`AI_AGENT.INTENT.SETS.NAMES.${set.name.toUpperCase()}`),
  }))
);

const selectedSet = computed({
  get: () => draft.intent?.intent_set ?? 'generic',
  set: value => setValue({ path: 'intent.intent_set' }, value),
});

const activeSet = computed(
  () => sets.value.find(set => set.name === selectedSet.value) || null
);

const systemEntries = computed(() =>
  activeSet.value
    ? Object.entries(activeSet.value.system).filter(
        ([key]) => key !== 'transfer_always'
      )
    : []
);

const routeEntries = computed(() =>
  activeSet.value ? Object.entries(activeSet.value.routes) : []
);

const isCustom = computed(() => selectedSet.value === 'custom');

const customRoutes = computed(() => {
  const routes = draft.intent?.custom_routes;
  return routes && typeof routes === 'object' ? routes : {};
});

const customRows = computed(() => Object.entries(customRoutes.value));

const newLabel = ref('');

const invalidLabel = label => !LABEL_PATTERN.test(label) || label.length > 64;

const customLabelError = label =>
  invalidLabel(label) ? t('AI_AGENT.INTENT.SETS.LABEL_INVALID') : '';

const setRouteDescription = (label, description) => {
  setValue(
    { path: 'intent.custom_routes' },
    { ...customRoutes.value, [label]: description }
  );
};

const addRoute = () => {
  const label = newLabel.value.trim();
  if (!label || invalidLabel(label) || label in customRoutes.value) return;
  setRouteDescription(label, '');
  newLabel.value = '';
};

const removeRoute = label => {
  const next = { ...customRoutes.value };
  delete next[label];
  setValue({ path: 'intent.custom_routes' }, next);
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const response = await new AiBotIntentSetsAPI(props.bot.id).get();
    sets.value = response.data;
  } finally {
    isLoading.value = false;
  }
});

defineExpose({ sets, selectedSet, activeSet, isCustom, customRows });
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="intent-set-picker">
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.INTENT.SETS.LABEL') }}
      </span>
      <Select
        v-model="selectedSet"
        :options="setOptions"
        data-testid="field-intent.intent_set"
      />
      <p class="text-xs text-n-slate-10">
        {{ $t('AI_AGENT.INTENT.SETS.HELP') }}
      </p>
    </div>

    <div v-if="activeSet" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h4 class="text-body font-medium text-n-slate-12">
          {{ $t('AI_AGENT.INTENT.SETS.ROUTES', { n: routeEntries.length }) }}
        </h4>
        <div
          v-for="[label, description] in routeEntries"
          :key="label"
          class="flex items-baseline gap-2 text-body-main"
        >
          <code
            class="shrink-0 rounded-md bg-n-solid-2 px-2 py-0.5 text-n-slate-12"
            >{{ label }}</code
          >
          <span class="text-n-slate-11">{{ description }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <h4 class="text-body font-medium text-n-slate-12">
          {{ $t('AI_AGENT.INTENT.SETS.SYSTEM') }}
        </h4>
        <div
          v-for="[key, label] in systemEntries"
          :key="key"
          class="flex items-baseline gap-2 text-body-main"
        >
          <span class="w-24 shrink-0 text-n-slate-10">{{ key }}</span>
          <code class="rounded-md bg-n-solid-2 px-2 py-0.5 text-n-slate-12">{{
            label
          }}</code>
        </div>
      </div>

      <div v-if="isCustom" class="flex flex-col gap-2">
        <h4 class="text-body font-medium text-n-slate-12">
          {{ $t('AI_AGENT.INTENT.SETS.CUSTOM_ROUTES') }}
        </h4>
        <p class="text-xs text-n-slate-10">
          {{ $t('AI_AGENT.INTENT.SETS.CUSTOM_HELP') }}
        </p>
        <div
          v-for="[label, description] in customRows"
          :key="label"
          class="flex items-start gap-2"
        >
          <code
            class="mt-1 shrink-0 rounded-md bg-n-solid-2 px-2 py-1.5 text-n-slate-12"
            >{{ label }}</code
          >
          <Input
            :model-value="description"
            :placeholder="$t('AI_AGENT.INTENT.SETS.DESCRIPTION_PLACEHOLDER')"
            :data-testid="`custom-route-${label}`"
            class="flex-1"
            @update:model-value="setRouteDescription(label, $event)"
          />
          <Button
            icon="i-woot-bin"
            slate
            sm
            class="mt-1 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="removeRoute(label)"
          />
        </div>
        <div class="flex items-start gap-2">
          <Input
            v-model="newLabel"
            :placeholder="$t('AI_AGENT.INTENT.SETS.LABEL_PLACEHOLDER')"
            :message="customLabelError(newLabel)"
            :message-type="customLabelError(newLabel) ? 'error' : 'info'"
            data-testid="custom-route-new-label"
            class="flex-1"
            @keyup.enter="addRoute"
          />
          <Button
            icon="i-lucide-plus"
            faded
            slate
            sm
            class="mt-1"
            :label="$t('AI_AGENT.INTENT.SETS.ADD')"
            @click="addRoute"
          />
        </div>
      </div>
    </div>
    <p v-else-if="!isLoading" class="text-xs text-n-slate-10">
      {{ $t('AI_AGENT.INTENT.SETS.LOADING') }}
    </p>
  </div>
</template>
